import {Box, Flex, Image, keyframes, Text} from '@chakra-ui/react'
import {CommonColors} from '@utils/CommonStyles'
import {useDispatch, useSelector} from 'react-redux'
import VolumeIcon from '@assets/svgs/volume.svg'
import {useEffect, useState} from 'react'
import learnPartThreeRequest from '@apis/learn-part-three'
import {
  updateAudio,
  updateIsDisableBtn,
} from '@redux/learn-part-three/learnPartThree'
import {Tooltip} from 'antd'
import {styleByTextTag} from '@utils'
import LocationIcon from '@assets/svgs/lern-part-three/location.svg'
import StarIcon from '@assets/svgs/lern-part-three/star.svg'
import FlagIcon from '@assets/svgs/lern-part-three/flag.svg'

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`

const PracticeLessonFrame2 = () => {
  const {
    currentContent,
    isShowSample,
    sessionId,
    practiceData,
    numberRecorded,
    isRecording,
  } = useSelector((state: any) => state.learnPartThree)
  const [practiceLessonFrame, setPracticeLessonFrame] = useState<any>(null)
  const dispatch = useDispatch()

  useEffect(() => {
    if (!sessionId) return
    getPracticeLessonFrame()
  }, [sessionId])

  useEffect(() => {
    if (numberRecorded > 0) {
      dispatch(updateIsDisableBtn(false))
    } else {
      dispatch(updateIsDisableBtn(true))
    }
  }, [numberRecorded])

  const getPracticeLessonFrame = async () => {
    try {
      const res = await learnPartThreeRequest.getPracticeLessonFrame({
        session_id: sessionId,
      })
      setPracticeLessonFrame(res?.data?.data)
    } catch (error) {}
  }

  const handlePlayAudio = (audio: string) => {
    if (numberRecorded > 0 || isRecording) return

    let audioData = {
      time: Date.now(),
      url: audio || '',
    }

    dispatch(updateAudio(audioData))
  }

  return (
    <Flex flexDirection={'column'} gap={'8px'} maxW={'600px'} mx={'auto'}>
      <Flex
        gap={'16px'}
        animation={`${slideUp} 0.5s ease-out forwards`}
        style={{
          opacity: 0,
          animationDelay: `${(0 + 1) * 0.4}s`,
        }}>
        <Flex
          flexShrink={0}
          flexDirection={'column'}
          minH={'100px'}
          alignItems={'center'}
          gap={'8px'}>
          <Image
            src={numberRecorded > 0 ? LocationIcon : VolumeIcon}
            alt="flag"
            width={'48px'}
            height={'48px'}
            cursor={'pointer'}
            onClick={() =>
              handlePlayAudio(
                isShowSample
                  ? practiceLessonFrame?.open?.content?.sample_audio
                  : practiceLessonFrame?.open?.content?.linking_phrase_audio,
              )
            }
          />
          <Box flex={1} w={'2px'} bg={'#E4E7EC'} />
        </Flex>
        <Flex flexDirection={'column'} pb={'16px'}>
          <Text fontSize={'16px'} fontWeight={'600'} color={'#98A2B3'}>
            {practiceLessonFrame?.open?.header}
          </Text>
          <Flex gap={'8px'} alignItems={'center'}>
            {practiceData ? (
              <Text fontSize={'16px'} color={CommonColors.primary}>
                {styleByTextTag(practiceData?.open?.text)}
              </Text>
            ) : (
              <>
                {isShowSample ? (
                  <Text fontSize={'16px'} color={'#475467'}>
                    {practiceLessonFrame?.open?.content?.sample}
                  </Text>
                ) : (
                  <Tooltip title={currentContent?.data?.popup?.open}>
                    <Text
                      fontSize={'16px'}
                      color={'#475467'}
                      cursor={'pointer'}>
                      {practiceLessonFrame?.open?.content?.linking_phrase}
                    </Text>
                  </Tooltip>
                )}
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
      {practiceLessonFrame?.main_ideas?.map((item: any, index: number) => (
        <Flex
          key={index}
          gap={'16px'}
          animation={`${slideUp} 0.5s ease-out forwards`}
          style={{
            opacity: 0,
            animationDelay: `${(index + 1) * 0.4}s`,
          }}>
          <Flex
            flexShrink={0}
            flexDirection={'column'}
            minH={'100px'}
            alignItems={'center'}
            gap={'8px'}>
            <Image
              src={numberRecorded > 0 ? StarIcon : VolumeIcon}
              alt="flag"
              width={'48px'}
              height={'48px'}
              cursor={'pointer'}
              onClick={() =>
                handlePlayAudio(
                  isShowSample
                    ? item?.sample_audio
                    : item?.linking_phrase_audio,
                )
              }
            />
            <Box flex={1} w={'2px'} bg={'#E4E7EC'} />
          </Flex>
          <Flex flexDirection={'column'} pb={'16px'}>
            <Text fontSize={'16px'} fontWeight={'600'} color={'#98A2B3'}>
              {item?.header}
            </Text>
            {practiceData ? (
              <Text fontSize={'16px'} color={CommonColors.primary}>
                {styleByTextTag(practiceData?.main_ideas[index]?.text)}
              </Text>
            ) : (
              <>
                {isShowSample ? (
                  <>
                    <Text fontSize={'16px'} color={'#475467'}>
                      {item?.sample}
                    </Text>
                  </>
                ) : (
                  <Flex gap={'8px'} alignItems={'center'}>
                    <Text fontSize={'16px'} color={'#475467'}>
                      {item?.linking_phrase}
                    </Text>
                    {item?.shortened_idea?.statement && (
                      <Tooltip title={currentContent?.data?.popup?.main_idea}>
                        <Text
                          cursor={'pointer'}
                          fontWeight={'500'}
                          color={CommonColors.primary}
                          bg={'#D6EBFF'}
                          borderRadius={'100px'}
                          px={'12px'}
                          py={'4px'}
                          border={'1px solid #99CEFF'}>
                          {item?.shortened_idea?.statement}
                        </Text>
                      </Tooltip>
                    )}
                  </Flex>
                )}
              </>
            )}
          </Flex>
        </Flex>
      ))}
      <Flex
        gap={'16px'}
        animation={`${slideUp} 0.5s ease-out forwards`}
        style={{
          opacity: 0,
          animationDelay: `${(practiceLessonFrame?.main_ideas?.length + 1) * 0.4}s`,
        }}>
        <Flex
          flexShrink={0}
          flexDirection={'column'}
          minH={'100px'}
          alignItems={'center'}
          gap={'8px'}>
          <Image
            src={numberRecorded > 0 ? FlagIcon : VolumeIcon}
            alt="flag"
            width={'48px'}
            height={'48px'}
            cursor={'pointer'}
            onClick={() =>
              handlePlayAudio(
                isShowSample
                  ? practiceLessonFrame?.close?.content?.sample_audio
                  : practiceLessonFrame?.close?.content?.linking_phrase_audio,
              )
            }
          />
          <Box flex={1} w={'2px'} bg={'#E4E7EC'} />
        </Flex>
        <Flex flexDirection={'column'} pb={'16px'}>
          <Text fontSize={'16px'} fontWeight={'600'} color={'#98A2B3'}>
            {practiceLessonFrame?.close?.header}
          </Text>
          <Flex gap={'8px'} alignItems={'center'}>
            {practiceData ? (
              <Text fontSize={'16px'} color={CommonColors.primary}>
                {styleByTextTag(practiceData?.close?.text)}
              </Text>
            ) : (
              <>
                {isShowSample ? (
                  <Text fontSize={'16px'} color={'#475467'}>
                    {practiceLessonFrame?.close?.content?.sample}
                  </Text>
                ) : (
                  <Tooltip title={currentContent?.data?.popup?.close}>
                    <Text
                      fontSize={'16px'}
                      color={'#475467'}
                      cursor={'pointer'}>
                      {practiceLessonFrame?.close?.content?.linking_phrase}
                    </Text>
                  </Tooltip>
                )}
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default PracticeLessonFrame2
